import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";
import { CalendarCheck, Hourglass, Coffee, Users, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

function StatCard({ label, value, icon: Icon, hint, testid, accent }) {
  return (
    <div data-testid={testid} className="surface-1 rounded-md p-6 hover:-translate-y-0.5 transition-all duration-300 group">
      <div className="flex items-start justify-between mb-6">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent ? "bg-[var(--gold)]/10 text-[var(--gold)] border border-[var(--gold)]/20" : "bg-white/5 text-[var(--text-muted)]"}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <ArrowUpRight className="w-3.5 h-3.5 text-[var(--text-dim)] opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="font-editorial text-5xl leading-none text-[var(--text-primary)]" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-xs text-[var(--text-muted)] mt-3 font-light tracking-wide">{label}</div>
      {hint && <div className="text-[10px] text-[var(--text-dim)] mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard");
        setData(data);
      } catch (e) {
        setErr("Keine Restaurantdaten verknüpft.");
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="p-10">
        <div className="surface-1 rounded-md p-8 text-center">
          <h2 className="font-heading text-2xl font-light mb-3">Kein Restaurant verknüpft</h2>
          <p className="text-sm text-[var(--text-muted)] mb-6">Ihr Konto ist noch keinem Restaurant zugeordnet.</p>
          {user?.role === "admin" && (
            <Link to="/admin" className="btn-primary inline-block" data-testid="dashboard-go-admin">SONARA Admin öffnen</Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-12 py-12 max-w-7xl mx-auto">
      <header className="mb-12 animate-fade-up">
        <h1 className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[var(--text-primary)]" data-testid="dashboard-title">{t("dashboard.title")}</h1>
        <p className="text-[var(--text-muted)] text-sm mt-3 font-light capitalize">
          {new Date().toLocaleDateString("de-CH", { weekday: "long", day: "numeric", month: "long" })}
        </p>
      </header>

      <div className="grid lg:grid-cols-2 gap-4 mb-10">
        <StatCard testid="stat-today-reservations" label={t("dashboard.today_reservations")} value={data?.today_reservations ?? "—"} icon={CalendarCheck} accent />
        <StatCard testid="stat-active-tables" label={t("dashboard.active_tables")} value={`${data?.active_tables ?? 0}/${data?.total_tables ?? 0}`} icon={Users} />
        <StatCard testid="stat-free-tables" label={t("dashboard.free_tables")} value={data?.free_tables ?? "—"} icon={Coffee} />
        <StatCard testid="stat-lounge-waiting" label={t("dashboard.lounge_waiting")} value={data?.lounge_waiting ?? 0} icon={Hourglass} accent={data?.lounge_waiting > 0} />
      </div>

      {/* ===== Nächste Ankünfte — volle Breite, editorial ===== */}
      <section className="surface-1 rounded-md p-8 mb-10">
        <div className="flex items-end justify-between mb-7">
          <h2 className="font-editorial italic text-[2rem] leading-none text-[var(--text-primary)]">{t("dashboard.upcoming")}</h2>
          <Link
            to="/dashboard/reservations"
            data-testid="dashboard-all-reservations"
            className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] hover:text-[var(--gold)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          >
            Alle ansehen →
          </Link>
        </div>
        <div>
          {(data?.upcoming || []).length === 0 && (
            <div className="font-editorial italic text-xl text-[var(--text-muted)] py-10 text-center">
              Heute Abend ist es ruhig.
            </div>
          )}
          {(data?.upcoming || []).map((r, idx) => {
            const d = r.date ? new Date(r.date) : null;
            const dateLabel = d ? d.toLocaleDateString("de-CH", { weekday: "short", day: "numeric", month: "short" }) : null;
            return (
              <div
                key={r.reservation_id}
                data-testid={`upcoming-${r.reservation_id}`}
                className={`grid grid-cols-12 items-center gap-4 py-5 ${idx > 0 ? "border-t border-white/[0.04]" : ""}`}
              >
                <div className="col-span-2 md:col-span-1 font-editorial text-[1.8rem] leading-none text-[var(--text-primary)]">
                  {r.time}
                </div>
                <div className="col-span-7 md:col-span-7">
                  <div className="font-editorial italic text-[1.35rem] leading-none text-[var(--text-primary)]">{r.guest_name}</div>
                  <div className="text-[12px] text-[var(--text-muted)] mt-1.5 tracking-wide flex flex-wrap items-center gap-x-2 gap-y-1">
                    {dateLabel && (<><span data-testid={`upcoming-date-${r.reservation_id}`}>{dateLabel}</span><span className="text-[var(--text-dim)]">·</span></>)}
                    <span>{r.party_size} {t("common.guests")}</span>
                    <span className="text-[var(--text-dim)]">·</span>
                    <span>{t(`reservations.types.${r.reservation_type}`) || r.reservation_type}</span>
                    {r.room_name && (<><span className="text-[var(--text-dim)]">·</span><span data-testid={`upcoming-room-${r.reservation_id}`}>{r.room_name}</span></>)}
                    {r.allergies && r.allergies.length > 0 && (
                      <>
                        <span className="text-[var(--text-dim)]">·</span>
                        <span className="italic">{r.allergies.join(", ")}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="col-span-3 md:col-span-4 text-right text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                  {t(`reservations.statuses.${r.status}`)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {(data?.waiting_list || []).length > 0 && (
        <section className="surface-1 rounded-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-normal flex items-center gap-2">
              <Hourglass className="w-4 h-4 text-gold" />
              {t("dashboard.waiting_list")}
            </h2>
            <Link to="/dashboard/lounge" className="text-xs text-gold hover:text-gold-light">Lounge Flow →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.waiting_list.map((r) => (
              <div key={r.reservation_id} className="surface-2 rounded-md p-4 border-l-2 border-gold pulse-gold">
                <div className="text-sm font-medium">{r.guest_name}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">{r.party_size} {t("common.guests")} · {r.time}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
